import { Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isSucceeded, isLoading } from 'conversations-async-data/async-data/operators/statusComparators';
// @ts-expect-error module not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { MARIGOLD_LIGHT } from 'HubStyleTokens/colors';
import styled from 'foundations-theming/utils';
import { usePubSubHealthCheck } from 'conversations-internal-pub-sub/redux/hooks/usePubSubHealthCheck';
import { useConnectedAccounts } from 'conversations-thread-data/connected-accounts/public';
import { getInboxId, getThreadId } from 'conversations-thread-data/thread-details/public/operators';
import { resolveEmailReplyConflicts } from 'conversations-thread-data/common-reply/public/operators/resolveEmailReplyConflicts';
import { CHANNEL_TYPES } from 'conversations-thread-data/channel-configuration/public/constants';
// @ts-expect-error module not typed - TODO: move to composer-ui
import { publishTypingMessage } from 'conversations-thread-view/outgoing-typing-messages/actions/publishTypingMessage';
// @ts-expect-error module not typed - TODO: move to composer-ui
import { getCurrentChatflowId } from 'conversations-thread-view/chatflows/selectors/getCurrentChatflowId';
// @ts-expect-error module not typed - TODO: move to composer-ui
import { hasReachedFbmReplyLimit } from 'conversations-thread-view/thread-histories/selectors/fbm24PlusOnePolicySelectors';
// TODO: move to composer-ui
import { hasReachedWhatsAppReplyLimit } from 'conversations-thread-view/thread-histories/selectors/whatsApp24PolicySelectors';
import { hasHostedEmailAccess, hasMessagingTrustedAccess } from 'conversations-thread-data/auth/public/selectors';
// TODO: move to composer-ui
import { useChatFlowConnectionStatus } from 'conversations-thread-data/chatflows/hooks/useChatFlowConnectionStatus';
import { EditorLoadingState } from '../../../editor-utilities/protected/components';
// TODO: move to composer-ui
import { DISCONNECTED } from 'conversations-thread-data/chatflows/constants/connectionStatuses';
import { getChannelType, getCheckConnectedAccounts, getRequireChatFlowConnectionStatus, getTypingIndicator, getIsMetadataEditableAndVisible, getRecipientMaxNew, getRecipientMaxReply } from 'conversations-thread-data/channel-configuration/public/operators';
import { EMAIL_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
// TODO: move to composer-ui
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { logCommonMessageSubmit } from '../utils/log';
import { useChannelInstances } from 'conversations-thread-data/channel-instances/public/hooks';
import { METADATA_BAR_HEIGHT, BASE_EDITOR_HEIGHT, EDITOR_MARGIN } from '../constants/replyEditorHeights';
import BaseCapabilitiesEditor from './BaseCapabilitiesEditor';
import { EditorCapabilitiesProvider } from 'composer-data/channel-capabilities/public/components';
import ComposerLoadingOverlay from './ComposerLoadingOverlay';
import ComposerErrorOverlay from './ComposerErrorOverlay';
import { reconcileDefaultSendersForChannel } from 'composer-data/common-reply/public/operators';
import { useBillingLimits } from 'conversations-thread-data/sms/public/hooks';
import { getSubscriptions } from 'conversations-internal-pub-sub/redux/selectors/subscriptionGetters';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledBaseCapabilitiesEditor = styled(BaseCapabilitiesEditor).withConfig({
  displayName: "CapabilitiesEditor__StyledBaseCapabilitiesEditor",
  componentId: "z2w5kk-0"
})([".ProsemirrorEditor__wrapper{background-color:", ";}"], MARIGOLD_LIGHT);
const defaultConflictResolver = (currentReply, __incomingEmptyReply) => currentReply;
const noop = () => {};
const falseSelector = () => false;
const CapabilitiesEditor = props => {
  const {
    asyncEmptyReply,
    renderSubmitButton,
    threadDetails,
    isMetadataEditorAbsolute = true,
    isNewEmailComposition = false,
    sendDisabledValidator = noop,
    capabilitiesBlob,
    currentChannelId,
    onCloseShepherd = noop,
    onChangeReplyChannel,
    onDraftChange = noop,
    originalGenericChannelId,
    submitHandler,
    isComposer = false,
    renderExistingFileSelect,
    replyRecommendationsEnabled,
    useWhatsAppCompose,
    latestCMFGenericChannelId,
    StyledWrapper = Fragment,
    defaultSendFromEnabled,
    macrosEnabled
  } = props;
  const requireChatFlowConnectionStatus = getRequireChatFlowConnectionStatus(capabilitiesBlob);
  const channelType = getChannelType(capabilitiesBlob);
  const checkConnectedAccounts = getCheckConnectedAccounts(capabilitiesBlob);
  const typingIndicator = getTypingIndicator(capabilitiesBlob);
  const maxRecipientNew = getRecipientMaxNew(capabilitiesBlob);
  const maxRecipientReply = getRecipientMaxReply(capabilitiesBlob);
  const dispatch = useDispatch();
  const threadId = getThreadId(threadDetails);
  const inboxId = getInboxId(threadDetails);
  const chatflowId = useSelector(isNewEmailComposition ? noop : getCurrentChatflowId);
  const isMessagingTrusted = useSelector(hasMessagingTrustedAccess);
  const ablySubscriptions = useSelector(getSubscriptions);
  const hasReachedFbmLimit = useSelector(isNewEmailComposition ? falseSelector : hasReachedFbmReplyLimit);
  const hasReachedWhatsAppLimit = useSelector(isNewEmailComposition ? falseSelector : hasReachedWhatsAppReplyLimit);
  const {
    billingLimitStatus
  } = useBillingLimits({
    currentEditorKey: currentChannelId
  });
  const hostedEmailEnabled = useSelector(hasHostedEmailAccess);
  const {
    canPublish
  } = usePubSubHealthCheck();
  const {
    asyncConnectedAccounts
  } = useConnectedAccounts({
    inboxId,
    filterNonGlobalAccounts: true,
    deferred: !checkConnectedAccounts
  });
  const {
    asyncChatFlowConnectionStatus
  } = useChatFlowConnectionStatus({
    chatflowId,
    deferred: !requireChatFlowConnectionStatus
  });
  const isMessengerDisconnected = requireChatFlowConnectionStatus && isSucceeded(asyncChatFlowConnectionStatus) ? getData(asyncChatFlowConnectionStatus) === DISCONNECTED : false;
  const handleTyping = useCallback(() => {
    if (typingIndicator) dispatch(publishTypingMessage(currentChannelId));
  }, [dispatch, typingIndicator, currentChannelId]);
  const {
    asyncChannelInstances
  } = useChannelInstances({
    inboxId
  });
  const getDisabledProps = useCallback(({
    canSubmit,
    contactPermissions,
    deliverabilityStatuses,
    reply,
    indexedAsyncFileUploads,
    isTestThread,
    maxFileLimit,
    editorState,
    hasContent,
    maxLength,
    maxSize
  }) => {
    /*
     * canSubmit||isNewEmailComposition: for new email compositions, the ably channel subscriptions aren't needed.
     * Originally added this to solve for the scenario of portals which doesn't have any threads (means no ably channel)
     * and user is trying to compose a new email.
     */

    return sendDisabledValidator({
      asyncConnectedAccounts,
      canSubmit: canSubmit || isNewEmailComposition,
      isNewEmailComposition,
      contactPermissions,
      indexedAsyncFileUploads,
      isTestThread,
      reply,
      emailDeliverabilityStatuses: deliverabilityStatuses,
      hasReachedFbmLimit: Boolean(hasReachedFbmLimit),
      hasReachedWhatsAppLimit,
      hostedEmailEnabled,
      isMessagingTrusted,
      isMessengerDisconnected,
      maxFileLimit,
      asyncChannelInstances,
      maxRecipientNew,
      maxRecipientReply,
      editorState,
      hasContent,
      maxLength,
      maxSize,
      billingLimitStatus,
      threadDetails,
      ablySubscriptions
    });
  }, [sendDisabledValidator, asyncConnectedAccounts, isNewEmailComposition, hasReachedFbmLimit, hasReachedWhatsAppLimit, hostedEmailEnabled, isMessagingTrusted, isMessengerDisconnected, asyncChannelInstances, maxRecipientNew, maxRecipientReply, billingLimitStatus, threadDetails, ablySubscriptions]);
  const BaseCapabilitiesEditorComponent = channelType === CHANNEL_TYPES.INTERNAL ? StyledBaseCapabilitiesEditor : BaseCapabilitiesEditor;
  const reconcileReplies = useWhatsAppCompose ? reconcileDefaultSendersForChannel : currentChannelId === EMAIL_GENERIC_CHANNEL_ID ? resolveEmailReplyConflicts : defaultConflictResolver;
  const onSubmit = useCallback((message, options) => {
    logCommonMessageSubmit(message, {
      currentChannelId,
      isComposer
    });
    submitHandler(message, options);
  }, [currentChannelId, isComposer, submitHandler]);
  const hasMetadata = getIsMetadataEditableAndVisible(capabilitiesBlob);
  const BASE_EDITOR_HEIGHT_WITH_MARGIN = BASE_EDITOR_HEIGHT + EDITOR_MARGIN;
  const EDITOR_MIN_HEIGHT = hasMetadata ? BASE_EDITOR_HEIGHT_WITH_MARGIN + METADATA_BAR_HEIGHT : BASE_EDITOR_HEIGHT_WITH_MARGIN;
  if (isLoading(asyncEmptyReply)) {
    return /*#__PURE__*/_jsx(EditorLoadingState, {
      minHeight: EDITOR_MIN_HEIGHT
    });
  }
  return /*#__PURE__*/_jsx(EditorCapabilitiesProvider, {
    editorConfigKey: currentChannelId,
    loadingState: /*#__PURE__*/_jsx(ComposerLoadingOverlay, {}),
    errorState: /*#__PURE__*/_jsx(ComposerErrorOverlay, {}),
    children: /*#__PURE__*/_jsx(ChannelCapabilitiesConfigContext.Provider, {
      value: {
        capabilitiesBlob,
        genericChannelId: currentChannelId,
        originalGenericChannelId,
        latestCMFGenericChannelId
      },
      children: /*#__PURE__*/_jsx(StyledWrapper, {
        children: /*#__PURE__*/_jsx(BaseCapabilitiesEditorComponent, {
          canSubmit: canPublish,
          emptyReply: getData(asyncEmptyReply),
          getDisabledProps: getDisabledProps,
          onDraftChange: onDraftChange,
          onSubmit: onSubmit,
          onTyping: handleTyping,
          reconcileReplies: reconcileReplies,
          renderSubmitButton: renderSubmitButton,
          threadDetails: threadDetails,
          isMetadataEditorAbsolute: isMetadataEditorAbsolute,
          onCloseShepherd: onCloseShepherd,
          onChangeReplyChannel: onChangeReplyChannel,
          isComposer: isComposer,
          renderExistingFileSelect: renderExistingFileSelect,
          replyRecommendationsEnabled: replyRecommendationsEnabled,
          useWhatsAppCompose: useWhatsAppCompose,
          defaultSendFromEnabled: defaultSendFromEnabled,
          macrosEnabled: macrosEnabled
        }, threadId)
      })
    })
  });
};
CapabilitiesEditor.displayName = 'CapabilitiesEditor';
CapabilitiesEditor.propTypes = {
  asyncEmptyReply: RecordPropType('AsyncData'),
  capabilitiesBlob: PropTypes.object,
  currentChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isComposer: PropTypes.bool,
  isMetadataEditorAbsolute: PropTypes.bool,
  isNewEmailComposition: PropTypes.bool,
  onCloseShepherd: PropTypes.func,
  renderExistingFileSelect: PropTypes.func,
  renderSubmitButton: PropTypes.func,
  sendDisabledValidator: PropTypes.func,
  submitHandler: PropTypes.func,
  threadDetails: RecordPropType('ThreadDetails').isRequired
};
export default CapabilitiesEditor;